import type Luminary from '@lumen5/luminary';
import { context, trace } from '@opentelemetry/api';

import type { EnhancedToolResult } from 'js/src/agent/types';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

export type AgentTask = {
    name: string;
    cancel: VoidFunction;
    run: () => Promise<EnhancedToolResult>;
    getConversationId?: () => string;
};

export type AgentOperation = (
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
) => AgentTask;

/**
 * Orchestrates sequential execution of multiple agent tasks.
 *
 * Each task wraps a RootAgent with its own conversation and tools.
 * The pipeline executes tasks one at a time, halting if any task fails.
 *
 * Note: Tools are not directly connected to the pipeline - they exist
 * within each agent's conversation and are called by the LLM during execution.
 *
 * Features:
 * - Sequential execution with failure halting (stops if isSuccess: false)
 * - Cancellation support (can cancel mid-execution)
 * - OpenTelemetry tracing for observability
 * - Single-use execution (cannot run twice)
 *
 * @example
 * ```typescript
 * const pipeline = new AgentTaskPipeline([
 *   createImproveMediaAgentOperation()(luminary, dispatch, getState),
 *   createBlueprintAgentOperation()(luminary, dispatch, getState),
 * ]);
 * const results = await pipeline.run();
 * ```
 */
export class AgentTaskPipeline {
    // TODO: convert to AgentOperation[] and bind the luminary, dispatch, getState internally
    // This will improve the developer experience when creating pipelines
    private agentTasks: AgentTask[];
    private running = false;
    private hasRun = false;
    private cancelled = false;
    private currentAgentTaskIndex = -1;

    constructor(agentTasks: AgentTask[]) {
        this.agentTasks = agentTasks;
    }

    cancel() {
        this.cancelled = true;

        if (this.currentAgentTaskIndex > -1) {
            const currentAgentTask = this.agentTasks[this.currentAgentTaskIndex];
            currentAgentTask.cancel();
        }
    }

    async run(): Promise<EnhancedToolResult[]> {
        if (this.running) {
            throw new Error('AgentPipeline is already running');
        }
        if (this.hasRun) {
            throw new Error('AgentPipeline has already been run');
        }
        this.running = true;
        this.hasRun = true;
        const results: EnhancedToolResult[] = [];

        const tracer = trace.getTracer('lumen5-luminary-operations');
        await new Promise<void>((resolve, reject) => {
            tracer.startActiveSpan('send-improvement-message', (span) => {
                const spanCtx = trace.setSpan(context.active(), span);
                context.with(spanCtx, async () => {
                    try {
                        for (let i = 0; i < this.agentTasks.length; i++) {
                            if (this.cancelled) {
                                break;
                            }
                            const agentTask = this.agentTasks[i];
                            this.currentAgentTaskIndex = i;
                            // eslint-disable-next-line no-console
                            console.log(`Running agent task: ${agentTask.name}`);

                            // eslint-disable-next-line promise/param-names
                            await new Promise<void>((resolveAgentSpan, rejectAgentSpan) => {
                                tracer.startActiveSpan(agentTask.name, async (agentSpan) => {
                                    try {
                                        const result = await agentTask.run();
                                        results.push(result);
                                        if (result.isSuccess === false) {
                                            // eslint-disable-next-line no-console
                                            console.error(`Agent ${agentTask.name} failed`, result);
                                            agentSpan.recordException(
                                                new Error(
                                                    `Agent ${agentTask.name} failed: ${result.result}`,
                                                ),
                                            );
                                        } else {
                                            // Log conversation link on success
                                            if (agentTask.getConversationId) {
                                                const conversationId =
                                                    agentTask.getConversationId();
                                                const conversationUrl = `${window.location.origin}/conversations/${conversationId}/`;
                                                // eslint-disable-next-line no-console
                                                console.log(
                                                    `[Agent Pipeline] ${agentTask.name} finished - ${conversationUrl}`,
                                                );
                                            }
                                        }
                                        resolveAgentSpan();
                                    } catch (e) {
                                        if (e instanceof Error) {
                                            agentSpan.recordException(e);
                                        }
                                        rejectAgentSpan(e);
                                    } finally {
                                        agentSpan.end();
                                    }
                                });
                            });

                            // Check if the last agent failed and break out
                            if (results[results.length - 1]?.isSuccess === false) {
                                break;
                            }
                        }
                        resolve();
                    } catch (e) {
                        if (e instanceof Error) {
                            span.recordException(e);
                        }
                        reject(e);
                    } finally {
                        span.end();
                    }
                });
            });
        });

        this.running = false;
        return results;
    }
}
